// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Building2, Users, Crown } from 'lucide-react';
import {
    getAllClients,
    getInternalCompany, setCompanyInternal,
    listProviders, setCompanyProvider,
} from '../../services/api';
import { toast } from 'react-toastify';

/**
 * Agents Anywhere — our own company. One job: mark which company is us
 * (the internal workspace) and manage its departments, experts, and team
 * leads. Clients and platform-wide numbers live elsewhere (Clients, the
 * Command Deck) so this surface stays about us, not them.
 */
const TeamOverview = () => {
    const navigate = useNavigate();
    const [clients, setClients] = useState([]);   // only to populate the setup picker
    const [internal, setInternal] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pickedCompany, setPickedCompany] = useState('');
    const [marking, setMarking] = useState(false);
    const [providers, setProviders] = useState([]);
    const [pickedProvider, setPickedProvider] = useState('');
    const [ownerAdmin, setOwnerAdmin] = useState(true);
    const [making, setMaking] = useState(false);

    const load = async () => {
        try {
            const [clientData, internalData, providerData] = await Promise.all([
                getAllClients(),
                getInternalCompany().catch(() => ({ internal: null })),
                listProviders().catch(() => ({ providers: [] })),
            ]);
            setClients(clientData || []);   // used only to populate the setup pickers
            setInternal(internalData?.internal || null);
            setProviders(providerData?.providers || []);
        } catch { toast.error('Could not load your company'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const markInternal = async () => {
        if (!pickedCompany) return;
        setMarking(true);
        try {
            await setCompanyInternal(pickedCompany, true);
            toast.success('Marked as your company');
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not mark the company');
        } finally { setMarking(false); }
    };

    const makeProvider = async () => {
        if (!pickedProvider) return;
        setMaking(true);
        try {
            await setCompanyProvider(pickedProvider, true, ownerAdmin);
            toast.success('Now a provider company');
            setPickedProvider('');
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not make the company a provider');
        } finally { setMaking(false); }
    };

    const removeProvider = async (p) => {
        if (!window.confirm(`"${p.name}" stops being a provider. Its experts and builder admins lose that standing, and its clients are served by ${internal?.name || 'the main provider'} again. Continue?`)) return;
        try {
            await setCompanyProvider(p.id, false);
            toast.success('No longer a provider');
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not change');
        }
    };

    const providerIds = new Set(providers.map((p) => p.id));
    const providerChoices = clients.filter((c) => c.id !== internal?.id && !providerIds.has(c.id));

    if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-gray-400" /></div>;

    return (
        <div className="space-y-8">
            {/* ── 1 · Our company ─────────────────────────────────────── */}
            <div>
                <h1 className="text-xl font-semibold text-gray-900">Agents Anywhere</h1>
                <p className="text-sm text-gray-500 mt-0.5">
                    Our own company: the departments, experts, and team leads that work across every client.
                </p>
            </div>

            {!internal ? (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
                    <div className="flex items-center gap-2 mb-1">
                        <Building2 className="w-4 h-4 text-amber-600" />
                        <h2 className="text-sm font-semibold text-gray-900">Set up Agents Anywhere</h2>
                    </div>
                    <p className="text-sm text-gray-600 mb-4 max-w-xl">
                        No company is marked as yours yet. Pick your own company below — its
                        departments become the routing fabric (which team owns builds, skills,
                        documents) and its roster holds your Team Leads and Experts.
                    </p>
                    <div className="flex items-center gap-2">
                        <select
                            value={pickedCompany}
                            onChange={(e) => setPickedCompany(e.target.value)}
                            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white min-w-[260px]"
                        >
                            <option value="">Pick your company…</option>
                            {clients.map((c) => (
                                <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
                            ))}
                        </select>
                        <button
                            onClick={markInternal}
                            disabled={!pickedCompany || marking}
                            className="inline-flex items-center gap-2 bg-gray-900 text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50 whitespace-nowrap"
                        >
                            {marking && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                            This is us
                        </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-3">
                        Don't see it? Create the company first (Clients → New Client, e.g. team@agentsanywhere.ai), then come back.
                    </p>
                </div>
            ) : (
                <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="w-8 h-8 rounded-lg bg-brand-50 grid place-items-center">
                                    <Building2 className="w-4 h-4 text-brand-600" />
                                </span>
                                <h2 className="text-base font-semibold text-gray-900">{internal.name}</h2>
                            </div>
                            <p className="text-sm text-gray-500 mt-1.5">
                                {internal.team_size} on the team · {internal.departments.length} department{internal.departments.length === 1 ? '' : 's'}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => navigate(`/consultant/clients/${internal.id}`)}
                                className="inline-flex items-center gap-1.5 text-xs font-semibold bg-brand-500 hover:bg-brand-600 text-white rounded-lg px-3.5 py-2 transition-colors whitespace-nowrap"
                            >
                                <Users className="w-3.5 h-3.5" /> Manage team &amp; departments
                            </button>
                            <button
                                onClick={async () => {
                                    if (!window.confirm(`"${internal.name}" stops being your company. Its departments stop routing until you pick a company again. Continue?`)) return;
                                    try {
                                        await setCompanyInternal(internal.id, false);
                                        toast.success('Unmarked — pick your company below');
                                        await load();
                                    } catch (e) {
                                        toast.error(e?.response?.data?.detail || 'Could not change');
                                    }
                                }}
                                className="text-xs text-gray-400 hover:text-gray-700 px-2 py-2 transition-colors"
                                title="Unmark this company and pick a different one"
                            >
                                Change
                            </button>
                        </div>
                    </div>

                    {internal.departments.length === 0 ? (
                        <div className="mt-4 rounded-xl bg-amber-50 border border-amber-100 px-4 py-3 text-sm text-amber-700">
                            No departments yet — nothing can route. Open "Manage team &amp; departments" and create the first one (e.g. Engineering, owning "build").
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
                            {internal.departments.map((d) => (
                                <div key={d.id} className="bg-gray-50 border border-gray-100 rounded-xl p-4">
                                    <div className="text-sm font-semibold text-gray-900">{d.name}</div>
                                    <div className="text-xs text-gray-500 mt-1 flex items-center gap-1.5">
                                        <Crown className="w-3 h-3 text-amber-500" />
                                        {d.lead_email || <span className="text-amber-600">no lead — assign one</span>}
                                    </div>
                                    <div className="flex items-center gap-1.5 flex-wrap mt-2.5">
                                        {(d.work_types || []).length === 0 ? (
                                            <span className="text-[11px] text-amber-600">owns nothing yet</span>
                                        ) : (
                                            d.work_types.map((t) => (
                                                <span key={t} className="text-[10.5px] font-sans bg-brand-50 text-brand-700 rounded-full px-2 py-0.5">{t}</span>
                                            ))
                                        )}
                                        <span className="text-[11px] text-gray-400 ml-auto">{d.members} member{d.members === 1 ? '' : 's'}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ── 2 · Provider companies ──────────────────────────────── */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                <h2 className="text-base font-semibold text-gray-900">Provider companies</h2>
                <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                    A provider adds its own clients, gets builder access, and its experts build for those clients only.
                    {internal ? ` ${internal.name} stays the main provider and sees every company.` : ''}
                </p>

                {providers.length > 0 && (
                    <div className="mt-4 divide-y divide-gray-100 border border-gray-100 rounded-xl">
                        {providers.map((p) => (
                            <div key={p.id} className="flex items-center justify-between gap-4 px-4 py-3 flex-wrap">
                                <div className="min-w-0">
                                    <div className="text-sm font-semibold text-gray-900">{p.name}</div>
                                    <div className="text-xs text-gray-500 mt-0.5">
                                        {p.email} · {p.team_size} on the team · {p.clients} client{p.clients === 1 ? '' : 's'} · {p.builder_admins} builder admin{p.builder_admins === 1 ? '' : 's'} · {p.experts} expert{p.experts === 1 ? '' : 's'}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => navigate(`/consultant/clients/${p.id}`)}
                                        className="inline-flex items-center gap-1.5 text-xs font-semibold border border-gray-200 hover:bg-gray-50 text-gray-800 rounded-lg px-3 py-2 whitespace-nowrap"
                                        title="Set builder admins and experts on its team"
                                    >
                                        <Users className="w-3.5 h-3.5" /> Team &amp; builder roles
                                    </button>
                                    <button
                                        onClick={() => removeProvider(p)}
                                        className="text-xs text-gray-400 hover:text-gray-700 px-2 py-2"
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="flex items-center gap-2 mt-4 flex-wrap">
                    <select
                        value={pickedProvider}
                        onChange={(e) => setPickedProvider(e.target.value)}
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white min-w-[260px]"
                    >
                        <option value="">Pick a company…</option>
                        {providerChoices.map((c) => (
                            <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
                        ))}
                    </select>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input type="checkbox" checked={ownerAdmin} onChange={(e) => setOwnerAdmin(e.target.checked)} />
                        Owner gets Builder admin and Builder expert
                    </label>
                    <button
                        onClick={makeProvider}
                        disabled={!pickedProvider || making}
                        className="inline-flex items-center gap-2 bg-gray-900 text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50 whitespace-nowrap"
                    >
                        {making && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                        Make provider
                    </button>
                </div>
                <p className="text-xs text-gray-400 mt-3">
                    The Owner's client projects come to them by default. More experts are set on the company's team (Team &amp; builder roles). Only an Owner can be a builder admin.
                </p>
            </div>
        </div>
    );
};

export default TeamOverview;
